import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Copy, Gift, Loader2, Wallet } from 'lucide-react';
import { motion } from 'motion/react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { rub } from '@/lib/currency';
import { numericDate } from '@/lib/date';
import { telegramShareUrl } from '@/lib/clipboard';
import { OptionRow, SectionLabel } from '@/components/ui/calm';
import { ProviderPills } from '@/components/ui/provider-pills';
import { SkeletonCard, SkeletonGrid } from '@/components/ui/skeleton';
import { getWebApp, haptic, openInvoiceUrl } from '@/lib/telegram';
import { cn } from '@/lib/utils';
import { periodMainNumber, periodMainUnit } from '@/lib/period';

/**
 * Подарочная подписка: покупка + список своих подарков.
 *
 * Раньше UI подарка жил bottom-sheet'ом внутри `/me` и открывался единственной
 * плиткой, которая рендерится ПОСЛЕ раннего выхода «нет своей подписки». Кнопка в
 * профиле при этом вела просто на `/me`, поэтому клиент жал «Подарить подписку» и
 * оказывался на главной. Отдельная страница чинит оба случая: она доступна по прямой
 * ссылке и НЕ требует своей активной подписки (бэк её тоже не требует).
 */
/** Кнопка «Навсегда» в сетке сроков: у тарифа нет периода, ключ вне диапазона дней. */
const LIFETIME_KEY = -1;

export const Route = createFileRoute('/gift')({
  component: GiftPage,
});

const GIFT_STATUS_LABELS: Record<string, { key: string; tone: string }> = {
  pending_payment: { key: 'Gift status pending', tone: 'text-[#f0b04e]' },
  paid_ready: { key: 'Gift status ready', tone: 'text-[#5fe3ad]' },
  claimed: { key: 'Gift status claimed', tone: 'text-[#8a8c93]' },
  expired: { key: 'Gift status expired', tone: 'text-[#f0656a]' },
};

/** ISO с бэка → «ДД.ММ.ГГГГ». Время в истории подарков не нужно. */
function giftDate(iso: string): string {
  return numericDate(iso) || '—';
}

function GiftPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    const tg = getWebApp();
    if (!tg?.BackButton) return;
    tg.BackButton.show();
    tg.MainButton.hide();
    const onBack = () => navigate({ to: '/profile' });
    tg.BackButton.onClick(onBack);
    return () => tg.BackButton.offClick(onBack);
  }, [navigate]);

  return (
    <motion.div
      initial={{ y: 14 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      className="p-4 space-y-6 pb-28"
    >
      <div className="px-1 pt-2">
        <h2 className="font-display text-2xl font-bold tracking-tight">{t('Gift title')}</h2>
        <p className="text-sm text-[#8a8c93] mt-1.5 leading-snug">
          {t('Gift desc')}

        </p>
      </div>

      <BuyGift />
      <MyGifts />
    </motion.div>
  );
}

function BuyGift() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [selectedDays, setSelectedDays] = useState<number | null>(null);
  // Пусто, пока не пришёл список: дальше первый способ сервера (СБП), а не CryptoBot.
  const [provider, setProvider] = useState<string>('');
  const [shareLink, setShareLink] = useState<string | null>(null);
  /** Ссылка уже рабочая (оплата с баланса прошла), а не «станет после оплаты счёта». */
  const [sharePaid, setSharePaid] = useState(false);

  const { data: plans, isPending: plansPending } = useQuery({
    queryKey: ['plans'],
    queryFn: () => api.plans(),
  });
  // «Навсегда» тоже дарят: отдельной кнопкой «∞» рядом со сроками.
  const lifetimeEnabled = api.getAuth().lifetimeEnabled;
  const { data: lifetimePlans } = useQuery({
    queryKey: ['plans', 'lifetime'],
    queryFn: () => api.plans('lifetime'),
    enabled: lifetimeEnabled,
  });
  const lifetimePlan = lifetimePlans?.[0] ?? null;
  const { data: providersData } = useQuery({
    queryKey: ['payment-providers'],
    queryFn: () => api.paymentProviders(),
  });
  const { data: profile } = useQuery({ queryKey: ['profile'], queryFn: () => api.profile() });

  const fallbackProvider = providersData?.providers?.[0] ?? '';

  useEffect(() => {
    const list = providersData?.providers;
    // 'balance' — валидный выбор, но в providersData его НЕТ (API отдаёт только
    // внешних провайдеров). Без этого исключения эффект мгновенно откатывал бы
    // выбор баланса на providers[0] — та же грабля, что чинили в /checkout.
    if (list && list.length > 0 && provider !== 'balance' && !list.includes(provider)) {
      setProvider(list[0]);
    }
    // `provider === ''` тоже сюда: пустой строки в списке нет.
  }, [providersData, provider]);

  const selectedPlan =
    plans?.find((p) => p.id === selectedPlanId) ??
    (lifetimePlan && lifetimePlan.id === selectedPlanId ? lifetimePlan : null);

  // Спецтарифы дарить нельзя: они покупаются один раз на клиента, и подарком этот
  // лимит обходился бы. Бэк отбивает такую покупку 409.
  const giftablePlans = useMemo(() => (plans ?? []).filter((p) => !p.isSpecial), [plans]);
  const periods = useMemo(
    () => Array.from(new Set(giftablePlans.map((p) => p.days))).sort((a, b) => a - b),
    [giftablePlans],
  );
  // Тот же двухуровневый выбор, что и на покупке: сначала срок, потом тариф. Плоский
  // список всех периодов подряд давал три карточки «Про» с разными ценами, и понять,
  // чем они отличаются, было нельзя вообще никак.
  const visiblePlans = useMemo(
    () =>
      selectedDays === null
        ? []
        : selectedDays === LIFETIME_KEY
          ? lifetimePlan
            ? [lifetimePlan]
            : []
          : giftablePlans.filter((p) => p.days === selectedDays).sort((a, b) => a.devices - b.devices),
    [giftablePlans, selectedDays, lifetimePlan],
  );
  const periodKeys = useMemo(
    () => (lifetimePlan ? [...periods, LIFETIME_KEY] : periods),
    [periods, lifetimePlan],
  );

  useEffect(() => {
    if (selectedDays === null && periods.length > 0) {
      setSelectedDays(periods.includes(30) ? 30 : periods[0]);
    }
  }, [periods, selectedDays]);
  const price = selectedPlan?.priceRub ?? 0;
  const balanceDisplay = (profile?.balance_display_minor ?? 0) / 100;
  const balanceEnough = !!selectedPlan && price > 0 && balanceDisplay >= price;
  const balanceShortfall = Math.max(0, Math.ceil(price - balanceDisplay));

  // Выбрали баланс, потом переключились на тариф подороже — метод перестал быть
  // применимым. Молча возвращаем внешнего провайдера, иначе кнопка предлагала бы
  // списать сумму, которой нет.
  useEffect(() => {
    if (provider === 'balance' && selectedPlan && !balanceEnough) setProvider(fallbackProvider);
  }, [provider, selectedPlan, balanceEnough, fallbackProvider]);

  // Замок от повторного тапа: биометрия и confirm асинхронные, висят секундами —
  // за это время второй тап успел бы создать второй подарок (и второе списание).
  const busyRef = useRef(false);

  const { mutate: createGift, isPending } = useMutation({
    mutationFn: (planId: string) => api.createGift({ plan_uuid: planId, provider }),
    onSettled: () => {
      busyRef.current = false;
    },
    onSuccess: (gift) => {
      openInvoiceUrl(gift.invoice_url);
      setShareLink(gift.share_url);
      setSharePaid(false);
      // Processing-экран сам пополлит статус платежа и уведёт на success, когда оплата
      // подтвердится. Ссылка-подарок останется в разделе «Мои подарки».
      if (gift.payment_id) {
        navigate({ to: '/processing/$paymentId', params: { paymentId: gift.payment_id } });
      }
    },
    onError: (e: Error) => toast.error(t('Gift create failed', { error: e.message })),
  });

  const { mutate: giftFromBalance, isPending: payingBalance } = useMutation({
    mutationFn: (planId: string) => api.createGiftFromBalance(planId),
    onSettled: () => {
      busyRef.current = false;
    },
    onSuccess: async (gift) => {
      // Баланс, список подарков и подписки поменялись разом — обновляем всё.
      await qc.invalidateQueries();
      setShareLink(gift.share_url);
      setSharePaid(true);
      haptic('success');
      toast.success(t('Gift paid'));
    },
    onError: (e: Error) => toast.error(e.message || t('Balance pay failed')),
  });

  const submit = async () => {
    if (!selectedPlanId || busyRef.current) return;
    busyRef.current = true;
    if (provider !== 'balance') {
      createGift(selectedPlanId);
      return;
    }
    // Списание мгновенное и без отката, поэтому спрашиваем подтверждение как на
    // обычной оплате: биометрия, а если недоступна — явный попап Telegram.
    const { biometric, confirmAction } = await import('@/lib/telegram');
    // Биометрию спрашиваем только если клиент её не отключил в профиле. Выбор при
    // первом разе предлагает экран оплаты (checkout), здесь просто уважаем решение.
    const result =
      api.getAuth().biometricConfirmEnabled === false
        ? 'unavailable'
        : await biometric.authenticate(t('Gift confirm buy'));
    if (result !== 'ok') {
      const ok = await confirmAction(
        t('Charge from balance', { amount: `${price} ₽` }),
        t('Payment confirm title'),
      );
      if (!ok) {
        busyRef.current = false;
        return;
      }
    }
    giftFromBalance(selectedPlanId);
  };

  return (
    <div className="space-y-3">
      <SectionLabel>{t('Pick a plan')}</SectionLabel>
      {plansPending ? (
        <SkeletonGrid
          count={5}
          cols={5}
          className="gap-2"
          itemClassName="h-[58px] min-h-0 rounded-[16px]"
        />
      ) : (
        // Шесть сроков с «навсегда» в сетке на пять оставляли его одного во втором
        // ряду; шесть колонок на 320px не вмещают подпись. Поэтому 3×2.
        <div
          className="grid gap-2"
          style={{ gridTemplateColumns: `repeat(${periodKeys.length > 5 ? 3 : 5}, minmax(0, 1fr))` }}
        >
          {periodKeys.map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => {
                haptic('light');
                setSelectedDays(d);
                // Иначе оплата уехала бы на тариф из прошлого периода.
                setSelectedPlanId(null);
              }}
              className={cn(
                'py-3 rounded-[16px] text-center transition-all active:scale-95',
                selectedDays === d ? 'calm-cta' : 'calm-card text-foreground',
              )}
            >
              <div className="font-bold text-base leading-tight">
                {d === LIFETIME_KEY ? '∞' : periodMainNumber(d)}
              </div>
              <div
                className={cn(
                  'text-[10px] mt-0.5',
                  selectedDays === d ? 'opacity-90' : 'text-[#8a8c93]',
                )}
              >
                {d === LIFETIME_KEY ? t('Lifetime forever') : periodMainUnit(d)}
              </div>
            </button>
          ))}
        </div>
      )}
      {plansPending ? (
        <>
          <SkeletonCard className="h-[74px] min-h-0 rounded-2xl" />
          <SkeletonCard className="h-[74px] min-h-0 rounded-2xl" />
        </>
      ) : (
        visiblePlans.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => {
              haptic('light');
              setSelectedPlanId(p.id);
            }}
            className={cn(
              'w-full text-left p-4 rounded-2xl border transition-all flex items-center justify-between active:scale-[0.98]',
              selectedPlanId === p.id ? 'border-accent bg-primary/8' : 'calm-card',
            )}
          >
            <div>
              <div className="font-bold text-foreground">{p.name}</div>
              <div className="text-[11px] text-[#8a8c93]">
                {p.isLifetime ? t('Lifetime forever') : t('Count days', { count: p.days })} ·{' '}
                {p.trafficGb !== null
                  ? t('Plan devices unlim gb', { devices: t('Count devices', { count: p.devices }), gb: p.trafficGb })
                  : t('Plan devices unlim', { devices: t('Count devices', { count: p.devices }) })}
              </div>
            </div>
            <div className="font-bold tabular text-foreground whitespace-nowrap shrink-0">{rub(p.priceRub)}</div>
          </button>
        ))
      )}

      {/* Баланс профиля — такой же способ оплаты, как провайдер. Показываем только
          когда выбран платный тариф: до выбора неизвестно, хватает ли денег. */}
      {selectedPlan && price > 0 && (
        <div className="space-y-2">
          <SectionLabel>{t('Pay from balance section')}</SectionLabel>
          {balanceEnough ? (
            <OptionRow
              icon={Wallet}
              iconTone="blue"
              title={t('From balance')}
              selected={provider === 'balance'}
              onSelect={() => {
                haptic('light');
                setProvider('balance');
              }}
              trailing={
                <span className="text-xs text-[#5fe3ad]">{rub(Math.floor(balanceDisplay))}</span>
              }
            />
          ) : (
            <OptionRow
              icon={Wallet}
              iconTone="neutral"
              title={t('From balance')}
              subtitle={t('Not enough', { amount: balanceShortfall })}
              onSelect={() => navigate({ to: '/topup' })}
              trailing={<span className="text-xs text-accent">{t('Top up arrow')}</span>}
            />
          )}
        </div>
      )}

      <ProviderPills
        providers={providersData?.providers ?? []}
        value={provider}
        onChange={setProvider}
      />

      <button
        type="button"
        disabled={!selectedPlanId || !provider || isPending || payingBalance}
        onClick={submit}
        className="w-full calm-cta py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50 active:scale-[0.98] transition-transform"
      >
        {(isPending || payingBalance) && <Loader2 className="w-4 h-4 animate-spin" />}
        {provider === 'balance'
          ? t('Gift from balance', { price })
          : t('Create invoice')}
      </button>

      {shareLink && (
        <div className="calm-card rounded-2xl p-4 space-y-3">
          <p className="text-sm text-[#8a8c93] leading-snug">
            {sharePaid
              ? t('Gift paid note')
              : t('Gift invoice note')}
          </p>
          <ShareRow link={shareLink} />
        </div>
      )}
    </div>
  );
}

function MyGifts() {
  const { t } = useTranslation();
  const { data: gifts, isPending } = useQuery({
    queryKey: ['my-gifts'],
    queryFn: () => api.myGifts(),
  });

  if (isPending) return <SkeletonCard className="h-[92px] rounded-[22px]" />;

  if (!gifts || gifts.length === 0) {
    return (
      <div className="calm-card rounded-[22px] py-8 px-5 text-center">
        <div className="w-[52px] h-[52px] rounded-[16px] bg-white/[0.05] flex items-center justify-center mx-auto mb-3 text-[#6b6d75]">
          <Gift className="w-[26px] h-[26px]" strokeWidth={1.6} />
        </div>
        <div className="font-display font-semibold text-[15px] text-[#c2c4ca]">
          {t('Gifts empty')}
        </div>
        <div className="text-[12.5px] text-[#6b6d74] mt-1.5">
          {t('Gifts empty desc')}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <SectionLabel>{t('My gifts')}</SectionLabel>
      {gifts.map((g) => {
        const status = GIFT_STATUS_LABELS[g.status];
        const statusLabel = status ? t(status.key) : g.status;
        const timeline = [
          t('Gift created at', { date: giftDate(g.created_at) }),
          g.paid_at ? t('Gift paid at', { date: giftDate(g.paid_at) }) : null,
        ].filter(Boolean) as string[];
        return (
          <div key={g.id} className="calm-card rounded-2xl p-4 space-y-2.5">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 font-bold leading-snug text-foreground line-clamp-2">{g.plan_name}</div>
              <div className={cn('text-[11px] font-bold shrink-0', status?.tone ?? 'text-[#8a8c93]')}>{statusLabel}</div>
            </div>
            <div className="space-y-1">
              <div className="text-[12px] text-[#6b6d74]">{timeline.join(' · ')}</div>
              {/* Кто активировал: бэк отдаёт «@nick» либо «ID 123456789» —
                  в таблице clients имени нет, только username и tg_id. */}
              {g.claimed_at && (
                <div className="text-[12px] text-[#8a8c93]">
                  {t('Gift claimed by', {
                    who: g.claimed_by_label ?? t('Gift recipient'),
                    date: giftDate(g.claimed_at),
                  })}
                </div>
              )}
            </div>
            {/* Ссылку показываем только пока подарок можно отдать: у активированного
                она уже сработала, у неоплаченного ещё не работает. */}
            {g.status === 'paid_ready' && g.share_url && <ShareRow link={g.share_url} />}
          </div>
        );
      })}
    </div>
  );
}

function ShareRow({ link }: { link: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      haptic('success');
      toast.success(t('Gift link copied'));
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error(t('Copy failed'));
    }
  };

  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={copy}
        className="w-full calm-card p-3 rounded-xl flex items-center justify-between gap-3 active:scale-[0.98] transition-transform"
      >
        <code className="text-xs truncate flex-1 text-left text-[#c2c4ca]">{link}</code>
        {copied ? (
          <Check className="w-4 h-4 text-[#5fe3ad] shrink-0" />
        ) : (
          <Copy className="w-4 h-4 text-accent shrink-0" />
        )}
      </button>
      <button
        type="button"
        onClick={() =>
          getWebApp()?.openTelegramLink(
            telegramShareUrl(link, t('Gift share text')),
          )
        }
        className="w-full calm-cta py-3 rounded-xl text-sm font-bold active:scale-[0.98] transition-transform"
      >
        {t('Share in Telegram')}
      </button>
    </div>
  );
}
